import { Box, useMediaQuery, useTheme } from "@mui/material";
import OrderCard from "./OrderCard";
import GenericDataDisplay from "../shared/genericDataDisplay";
import Order from "@/app/interfaces/Order";
import { useOrderHistoryFilters } from "@/app/hooks/useOrderHistoryFilter";
import OrderHistoryFilterState from "@/app/interfaces/OrderHistoryFilter";
import dataService from "@/app/services/dataService";
import { omit } from "lodash";
import { OrderHistoryFilterFieldEnum } from "@/enums/orderHistoryFilterFiledEnum";
import FilterHelper from "@/app/helpers/FilterHelper";
import OrderHistoryFilterPayload from "@/app/interfaces/payload/OrderHistoryFilterPayload";
import { useOrderHistoryTable } from "@/app/hooks/useOrderHistoryTable";
import { useSearchParams } from "react-router-dom";
import { useGlobalNotification } from "../header/GlobalNotificationContext";
import {
  DEFAULT_PAGE_NUMBER,
  ERROR_MESSAGE,
  PAGINATION_PAGE_SIZE_OPTIONS,
} from "@/app/constants/constants";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import {
  loadFromStorage,
  saveToStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";
import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import { RootState } from "@/app/store/store";
import { useSelector } from "react-redux";
import { GenericDataDisplayTypeEnum } from "@/enums/genericDataDisplayType";

const QUERY_KEY_BASE = "orders";
const NO_ORDERS_MESSAGE =
  "No orders could be found, please check your search criteria and try again";

const OrderHistory = () => {
  const { filters } = useOrderHistoryFilters();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();
  const { setNotification } = useGlobalNotification();
  const { updatePagination, updateTotalCount } = useOrderHistoryTable();
  const searchKey = searchParams.toString();
  const hasSearchParams = searchKey.length > 0;
  const searchKeyForQuery = hasSearchParams ? searchKey : "no-params";
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";
  const retriggerKey = filters[OrderHistoryFilterFieldEnum.RETRIGGER_KEY] ?? 0;
  const filtersForApi =
    searchKey === "tab=quotes"
      ? FilterHelper.getSignatureForQuotes()
      : omit(filters, [
          OrderHistoryFilterFieldEnum.ADVANCED_SEARCH_EXPANDED,
          OrderHistoryFilterFieldEnum.RETRIGGER_KEY,
        ]);

  const renderOrderCard = (order: Order, index: number) => (
    <OrderCard order={order} defaultExpanded={index === 0 || isMobile} />
  );
  const getOrderId = (order: Order) => order.id;

  const cachePageSize = loadFromStorage<number>(
    userId,
    STORAGE_KEYS.ORDER_PAGE_SIZE
  );

  const cachePage = loadFromStorage<number>(
    userId,
    STORAGE_KEYS.ORDER_PAGE_NUMBER
  );

  const fetchFilteredOrdersData = async (
    filters: Omit<
      OrderHistoryFilterState,
      | OrderHistoryFilterFieldEnum.ADVANCED_SEARCH_EXPANDED
      | OrderHistoryFilterFieldEnum.RETRIGGER_KEY
    >,
    page: number,
    pageSize: number
  ): Promise<{ items: Order[]; total: number }> => {
    let payload: OrderHistoryFilterPayload = {
      ...filters,
      page,
      pageSize,
      [OrderHistoryFilterFieldEnum.MANUFACTURERS]:
        FilterHelper.extractIdFromOptions(filters.manufacturers),
      [OrderHistoryFilterFieldEnum.ORDER_SHIP_TOS]:
        FilterHelper.extractIdFromOptions(filters.orderShipTos),
      [OrderHistoryFilterFieldEnum.ORDER_STATUS_IDS]:
        FilterHelper.extractIdFromOptions(filters.orderStatusIds),
      [OrderHistoryFilterFieldEnum.ORDER_SUB_STATUS_IDS]:
        FilterHelper.extractIdFromOptions(filters.orderSubStatusIds),
      [OrderHistoryFilterFieldEnum.TYPE_IDS]: FilterHelper.extractIdFromOptions(
        filters.typeIds
      ),
      [OrderHistoryFilterFieldEnum.CATEGORY_IDS]:
        FilterHelper.extractIdFromOptions(filters.categoryIds),
    };

    const tabKey = Array.from(searchParams.keys())[0];
    const tabValue = tabKey ? searchParams.get(tabKey) : null;

    if (tabKey && tabValue) {
      const key = FilterHelper.mapSearchParamToKey(tabKey);
      const value = FilterHelper.shouldValueBeMapped(tabKey)
        ? FilterHelper.mapQueryValueToFilterValue(tabValue)
        : tabValue;
      payload = FilterHelper.applySearchParamToPayload(
        payload,
        key,
        value,
        FilterHelper.shouldParamBeArray(tabKey)
      );
    }

    saveToStorage(userId, STORAGE_KEYS.ORDER_FILTERS, filters);

    const result = await dataService.getOrderHistory(payload);

    if (result.success) {
      updatePagination(page, pageSize);
      updateTotalCount(result.data.totalCount);

      saveToStorage(userId, STORAGE_KEYS.ORDER_PAGE_SIZE, pageSize);
      saveToStorage(userId, STORAGE_KEYS.ORDER_PAGE_NUMBER, page);

      return { items: result.data.orders, total: result.data.totalCount };
    } else {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);

      return { items: [], total: 0 };
    }
  };

  return (
    <Box>
      <GenericDataDisplay<
        Order,
        Omit<
          OrderHistoryFilterState,
          | OrderHistoryFilterFieldEnum.ADVANCED_SEARCH_EXPANDED
          | OrderHistoryFilterFieldEnum.RETRIGGER_KEY
        >
      >
        externalFilters={filtersForApi}
        queryKeyBase={[
          QUERY_KEY_BASE,
          retriggerKey.toString(),
          searchKeyForQuery,
        ]}
        resetOn={[filtersForApi]}
        wrapperStyleProps={{
          sx: { backgroundColor: "#f0f0f0", p: 2, borderRadius: 2 },
        }}
        mode={GenericDataDisplayTypeEnum.LIST}
        renderItem={renderOrderCard}
        itemKeyAccessor={getOrderId}
        fetchPage={(page, pageSize, filters) =>
          fetchFilteredOrdersData(filters, page, pageSize)
        }
        noDataMessage={NO_ORDERS_MESSAGE}
        defaultPageSize={cachePageSize ?? PAGINATION_PAGE_SIZE_OPTIONS[0]}
        defaultPageNumber={cachePage ?? DEFAULT_PAGE_NUMBER}
      />
    </Box>
  );
};

export default OrderHistory;
